import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

export function formatDate(d: string | Date | undefined | null, lang: 'en' | 'te' = 'en', opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }) {
  if (!d) return '';
  return new Intl.DateTimeFormat(lang === 'te' ? 'te-IN' : 'en-IN', opts).format(new Date(d));
}

export function formatDateTime(d: string | Date | undefined | null, lang: 'en' | 'te' = 'en') {
  return formatDate(d, lang, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function relativeTime(d: string | Date) {
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (Math.abs(diff) < 60) return rtf.format(-Math.round(diff), 'second');
  if (Math.abs(diff) < 3600) return rtf.format(-Math.round(diff / 60), 'minute');
  if (Math.abs(diff) < 86400) return rtf.format(-Math.round(diff / 3600), 'hour');
  return rtf.format(-Math.round(diff / 86400), 'day');
}

export const telHref = (p?: string) => (p ? `tel:${p.replace(/[^\d+]/g, '')}` : undefined);
export const waHref = (p?: string) => (p ? `https://wa.me/${p.replace(/\D/g, '').replace(/^(?!91)(\d{10})$/, '91$1')}` : undefined);
export const directionsHref = (coords?: number[]) => (coords?.length === 2 ? `https://www.openstreetmap.org/directions?to=${coords[1]}%2C${coords[0]}` : undefined);
export const googleMapsHref = (coords?: number[]) => (coords?.length === 2 ? `https://www.google.com/maps/dir/?api=1&destination=${coords[1]},${coords[0]}` : undefined);

/** Mirrors the backend URL allowlist for rendering admin-provided links. */
export function isSafeUrl(url?: string) {
  if (!url) return false;
  const v = url.trim();
  if (v.startsWith('/')) return !v.startsWith('//');
  if (v.startsWith('#')) return true;
  try {
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(new URL(v).protocol);
  } catch {
    return false;
  }
}

export const isExternal = (url: string) => /^(https?:|mailto:|tel:)/i.test(url);

export const uid = (p = 'b') => `${p}-${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-3)}`;

export const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', in_review: 'In review', scheduled: 'Scheduled', published: 'Published', unpublished: 'Unpublished', archived: 'Archived',
  pending_review: 'Pending review', under_review: 'Under review', changes_requested: 'Changes requested', approved: 'Approved', rejected: 'Rejected',
  open: 'Open', in_progress: 'In progress', resolved: 'Resolved', dismissed: 'Dismissed',
};
